import { useMemo, useState } from 'react';

import { startOfDay, subMonths, subWeeks } from 'date-fns';
import { useLocation } from 'react-router-dom';

import { ActivityFeed } from '@modules/_shared/components/feed-v2/ActivityFeed';
import { type DateRange } from '@modules/_shared/components/feed-v2/components/DateRangeSelect';
import { USER_FEED_V2 } from '@modules/users/userQueries';

type UserFeedV2Props = {
  userId: string;
  phoneNumber?: string | null;
  scrollParent: HTMLElement | null;
  initialDateRange?: DateRange;
};

const getAfterDate = (range: DateRange): string | null => {
  const now = startOfDay(new Date());
  switch (range) {
    case 'week':
      return subWeeks(now, 1).toISOString();
    case 'month':
      return subMonths(now, 1).toISOString();
    case '3months':
      return subMonths(now, 3).toISOString();
    case 'all':
      return null;
  }
};

const DATE_RANGES: DateRange[] = ['week', 'month', '3months', 'all'];

const getRangeContaining = (
  createdAt: string | undefined,
  initialDateRange: DateRange,
): DateRange => {
  if (createdAt == null) {
    return initialDateRange;
  }
  const created = new Date(createdAt);
  return (
    DATE_RANGES.slice(DATE_RANGES.indexOf(initialDateRange)).find(range => {
      const afterDate = getAfterDate(range);
      return afterDate == null || new Date(afterDate) <= created;
    }) ?? 'all'
  );
};

const UserFeedV2 = ({
  userId,
  phoneNumber,
  scrollParent,
  initialDateRange = 'week',
}: UserFeedV2Props) => {
  const location = useLocation();
  const [dateRange, setDateRange] = useState<DateRange>(() =>
    getRangeContaining(
      (location.state as { focusActivityCreatedAt?: string } | null)
        ?.focusActivityCreatedAt,
      initialDateRange,
    ),
  );
  const [overdue, setOverdue] = useState(false);

  const variables = useMemo(() => {
    const base: Record<string, unknown> = { user_id: userId };
    // overdue and after_date are cumulative server-side: the date window still
    // applies, overdue just additionally keeps only past-due activities.
    const afterDate = getAfterDate(dateRange);
    if (afterDate != null) {
      base.after_date = afterDate;
    }
    if (overdue) {
      base.overdue = true;
    }
    return base;
  }, [userId, dateRange, overdue]);

  return (
    <ActivityFeed
      parentType="User"
      parentId={userId}
      fallbackPhoneNumber={phoneNumber}
      query={USER_FEED_V2}
      variables={variables}
      scrollParent={scrollParent}
      dateRange={dateRange}
      onDateRangeChange={setDateRange}
      overdue={overdue}
      onOverdueChange={setOverdue}
    />
  );
};

export default UserFeedV2;
